import { memo, useState } from "react";
import {
  FloatingPortal,
  autoUpdate,
  offset,
  useFloating,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { Handle, Position } from "@xyflow/react";
import { Outcome } from "@unlikelyai-magic/shared/data-access/api";
import { Paragraph } from "@unlikelyai-magic/ui/typography";
import { cn } from "@unlikelyai-magic/utils";
import { UpdateType, type UpdatedNodeInfo } from "../types";

interface AutomaticEditNodeData {
  label: string;
  value: Outcome;
  question: string;
  updateType: UpdateType;
  updateInfo: UpdatedNodeInfo;
  reasoning?: string;
}

export interface AutomaticEditConditionNodeProps {
  id: string;
  data: AutomaticEditNodeData;
  isConnectable: boolean;
}

export const AutomaticEditConditionNode = memo(
  ({ id, data, isConnectable }: AutomaticEditConditionNodeProps) => {
    const [open, setOpen] = useState(false);
    const { refs, floatingStyles, context } = useFloating({
      open,
      onOpenChange: setOpen,
      placement: "top",
      middleware: [offset(8)],
      whileElementsMounted: autoUpdate,
    });
    const hover = useHover(context, { move: false, restMs: 50 });
    const role = useRole(context, { role: "tooltip" });
    const { getReferenceProps, getFloatingProps } = useInteractions([
      hover,
      role,
    ]);

    const { updateInfo, updateType } = data;

    const getUpdateTypeTag = () => {
      if (updateType === UpdateType.PRE_EVALUATED) {
        return "(PRE-EVALUATED)";
      }
      if (updateType === UpdateType.LOGICALLY_ELIMINATED) {
        return "(LOGICALLY ELIMINATED)";
      }
      return null;
    };

    return (
      <>
        <Handle
          type="target"
          position={Position.Top}
          isConnectable={isConnectable}
          className="opacity-0"
        />
        <div
          ref={refs.setReference}
          {...getReferenceProps()}
          className={cn(
            "px-4 py-3 rounded-lg border-2 min-w-[200px] max-w-[400px]",
            "transition-all duration-200",
            "bg-blue-50 border-blue-200 relative overflow-hidden"
          )}
        >
          {/* Hashing effect */}
          <div
            className="absolute inset-0 pointer-events-none opacity-10"
            style={{
              backgroundImage: `repeating-linear-gradient(45deg, #3b82f6 0, #3b82f6 1px, transparent 0, transparent 50%)`,
              backgroundSize: '10px 10px'
            }}
          />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-blue-200 text-blue-800">
                AUTOMATIC
              </span>
              {getUpdateTypeTag() && (
                <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-tight">
                  {getUpdateTypeTag()}
                </span>
              )}
            </div>
            <Paragraph className="font-semibold text-blue-900">
              {data.question ?? data.label}
            </Paragraph>
          </div>
        </div>
        <Handle
          type="source"
          position={Position.Bottom}
          isConnectable={isConnectable}
          className="opacity-0"
        />

        {open && (
          <FloatingPortal>
            <div
              ref={refs.setFloating}
              style={floatingStyles}
              {...getFloatingProps()}
              className="max-w-md rounded-lg p-3 bg-white border border-gray-200 shadow-lg z-[9999]"
            >
              <div className="text-sm text-gray-700 mb-2">
                {updateInfo.explanation}
              </div>
              {updateInfo.note && (
                <div className="text-sm text-blue-600 italic">
                  Note: {updateInfo.note}
                </div>
              )}
            </div>
          </FloatingPortal>
        )}
      </>
    );
  }
);

AutomaticEditConditionNode.displayName = "AutomaticEditConditionNode";
